import MarkdownIt from 'markdown-it'

/**
 * Comment and description bodies are markdown written by a person **or an MCP agent**, and an agent
 * can be steered by whatever text it was fed. So the content is untrusted input.
 *
 * `html: false` means raw HTML in the source is escaped rather than passed through, which removes
 * the main XSS vector at the source instead of bolting on a sanitizer and hoping it is configured
 * correctly. On top of that we allow only http, https and mailto links — a `javascript:` href would
 * otherwise survive the markdown link syntax.
 */
const SAFE_SCHEMES = /^(https?:|mailto:)/i

const md = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: true,
})

const defaultLinkOpen =
  md.renderer.rules.link_open ??
  ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options))

md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  const token = tokens[idx]!
  const href = token.attrGet('href')

  if (typeof href === 'string' && !SAFE_SCHEMES.test(href)) {
    token.attrSet('href', '#')
    token.attrSet('data-blocked-scheme', 'true')
  }

  token.attrSet('rel', 'noopener noreferrer')
  token.attrSet('target', '_blank')
  return defaultLinkOpen(tokens, idx, options, env, self)
}

/**
 * Highlights `@osoba` in rendered text.
 *
 * A markdown-it rule, not a regex over the finished HTML: a regex would happily rewrite the inside
 * of a code block, a URL or an attribute value. The renderer already knows which text is prose and
 * which is not, so it does the deciding.
 *
 * Whether a handle is a real person is the server's call (services/mentions.ts) — here it is purely
 * visual, so a typo looks like a mention that simply did not find anybody.
 */
const MENTION_TEXT = /(^|[^\w@])@([\p{L}\p{N}._-]{2,60})/gu

/** Wzięty z instancji, nie z podścieżki paczki: markdown-it niesie własne typy, a @types/markdown-it
    swoje, i te dwa zestawy się nie zgadzają. */
type RenderRule = NonNullable<typeof md.renderer.rules.text>

/**
 * Podświetlamy TYLKO istniejące osoby.
 *
 * Podświetlenie mówi „ta osoba dostanie powiadomienie". Gdyby zapalało się też przy literówce,
 * człowiek zobaczyłby niebieskie `@ktoscObcy` i uznał, że kogoś zawiadomił — a serwer nie ma kogo
 * powiadomić i nikt się nie dowie. Lepiej, żeby literówka wyglądała jak literówka.
 */
let knownHandles = new Set<string>()

export function setKnownMentionHandles(handles: string[]): void {
  knownHandles = new Set(handles.map((h) => h.toLowerCase()))
}

function mentionPlugin(instance: typeof md): void {
  const defaultText: RenderRule =
    instance.renderer.rules.text ??
    ((tokens, idx) => instance.utils.escapeHtml(tokens[idx]!.content))

  const withMentions: RenderRule = (tokens, idx, options, env, self) => {
    // Runs on already-escaped output, so the span is the only markup we ever add here.
    const rendered = defaultText(tokens, idx, options, env, self)
    return rendered.replace(MENTION_TEXT, (match: string, before: string, handle: string) =>
      knownHandles.has(handle.toLowerCase())
        ? `${before}<span class="mention">@${handle}</span>`
        : match,
    )
  }

  instance.renderer.rules.text = withMentions
}

md.use(mentionPlugin)

export function renderMarkdown(source: string): string {
  return md.render(source)
}

export function renderMarkdownInline(source: string): string {
  return md.renderInline(source)
}
